import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, apiError } from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import { fmtDateTime } from "@/lib/date";
import { Search, Loader2, CheckCircle2, Clock, XCircle, FileSearch } from "lucide-react";

const STATUS = {
  submitted: { label: "Submitted", color: "bg-sky-100 text-sky-700", icon: Clock },
  under_review: { label: "Under Review", color: "bg-amber-100 text-amber-700", icon: FileSearch },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  rejected: { label: "Not Accepted", color: "bg-rose-100 text-rose-700", icon: XCircle },
};

export default function TrackAdmission() {
  const [no, setNo] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [app, setApp] = useState(null);

  const track = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/public/admissions/track`, { params: { application_no: no, phone } });
      setApp(data);
    } catch (err) { setApp(null); toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const st = app && (STATUS[app.status] || STATUS.submitted);

  return (
    <div className="pub-mesh min-h-[80vh]">
      <div className="mx-auto max-w-3xl px-5 py-14" data-testid="track-page">
        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Admission Status</p>
        <h1 className="mt-1 text-3xl font-bold font-head sm:text-4xl">Track your application</h1>
        <form onSubmit={track} className="pub-card mt-8 grid gap-4 p-6 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <label className="block"><span className="mb-1.5 block text-xs font-semibold text-slate-600">Application Number</span><input className="pub-input font-mono uppercase" placeholder="APP-SRUPS-2026-0001" value={no} onChange={(e) => setNo(e.target.value)} data-testid="track-app-no" /></label>
          <label className="block"><span className="mb-1.5 block text-xs font-semibold text-slate-600">Registered Mobile</span><input className="pub-input" placeholder="10-digit mobile" maxLength={10} value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="track-phone" /></label>
          <button className="pub-btn" disabled={busy || !no || phone.length < 10} data-testid="track-submit-btn">{busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Track</button>
        </form>

        {app && (
          <div className="pub-card mt-6 p-6" data-testid="track-result">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                {app.documents?.photo && <img src={fileUrl(`/api/public/files/${app.documents.photo}`)} alt="" className="h-16 w-16 rounded-xl object-cover ring-1 ring-slate-200" />}
                <div>
                  <p className="font-mono text-xs text-slate-500">{app.application_no}</p>
                  <h2 className="text-xl font-semibold font-head">{app.student_name}</h2>
                  <p className="text-sm text-slate-500">Class {app.class_applying} · {app.school_name}</p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${st.color}`} data-testid="track-status"><st.icon size={14} /> {st.label}</span>
            </div>
            {app.admission_no && <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800">Admission confirmed · Admission No <b className="font-mono">{app.admission_no}</b>. Please visit the school office with original documents.</p>}
            {app.remarks && <p className="mt-3 text-sm text-slate-600"><b>School remarks:</b> {app.remarks}</p>}
            <ol className="mt-6 space-y-3 border-l-2 border-slate-200 pl-5">
              {(app.timeline || []).map((t, i) => {
                const s = STATUS[t.status] || STATUS.submitted;
                return (
                  <li key={i} className="relative text-sm">
                    <span className={`absolute -left-[27px] top-0.5 flex h-4 w-4 items-center justify-center rounded-full ${s.color}`}><s.icon size={10} /></span>
                    <p className="font-medium">{s.label}</p>
                    <p className="text-xs text-slate-500">{fmtDateTime(t.at)} {t.note ? `· ${t.note}` : ""}</p>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
